import React, { Fragment, useMemo } from 'react';
import { always, complement, cond, isEmpty, pipe, prop, propEq, find, sortBy } from 'ramda';
import { useIntl } from 'gatsby-plugin-intl';
import { findByType } from '../../utils';
import { ELEMENTS_TYPES, BLOCK_ELEMENTS } from '../../utils/constants';
import { CardLeft } from '../cardLeft';
import {
  Title,
  Container,
  Side,
  Main,
  CardWrapper,
  Card,
  NoValueCard,
  CardTitle,
  CardHeader,
  CardContent,
} from './columnCard.styles';
import { renderWhenTrue } from '../../utils/rendering';
import { CustomTooltip } from '../customTooltip';
import { useCardLeft } from '../../hooks/useCardLeft';
import { CardContainer, CardToggleButton, CardToggleCloseIcon } from '../cardLeft/cardLeft.styles';
import {InfoIconSVG, YesIconSVG, NoIconSVG} from './columnCard.constants'

const renderTitle = ( {value} ) => {
  if(!value) return null
  const intl = useIntl();
  return <Title>{intl.formatMessage({ id: value })}</Title>;
};

const renderCardTitle = ({ value }, hasValue ) => {
  const {formatMessage} = useIntl()
  if(!value) return null
  if (hasValue.value === 'false') {
    return (
      <CardTitle>
        <NoIconSVG />
        {formatMessage({ id: value })}
      </CardTitle>
    )
  } if (hasValue.value === 'true') {
    return (
      <CardTitle>
        <YesIconSVG />
        {formatMessage({ id: value })}
      </CardTitle>
    )
  } else return <CardTitle>{formatMessage({ id: value })}</CardTitle>
}

const renderTooltip = (connection, citation) => {
  if(!connection.value && !citation.value) return null
  return (
    <CustomTooltip connection={connection} citation={citation} />
  )
}

const renderCardContent = ({ value }) => {
  const {formatMessage} = useIntl()
  if(!value) return null
  return (
    <CardContent>
      {formatMessage({ id: value })}
    </CardContent>
  )
}

const renderColumnCard = ({ elements = [] }, key) => {
  const cardTitle = find(propEq('name', 'plain_text'))(elements) || {};
  const hasValue = find(propEq('name', 'has_value'))(elements) || {};
  const markdown = find(propEq('name', 'markdown'))(elements) || {};
  const connection = find(propEq('name', 'connection'))(elements) || {};
  const citation = find(propEq('name', 'citation'))(elements) || {};
  if (hasValue.value === 'false'){
    return (
      <NoValueCard>
        <CardHeader>
          {renderCardTitle(cardTitle, hasValue)}
        </CardHeader>
      </NoValueCard>
    )
  } else {
    return (
      <Card>
        <CardHeader>
          {renderCardTitle(cardTitle, hasValue)}
          {renderTooltip(connection, citation)}
        </CardHeader>
        {renderCardContent(markdown)}
      </Card>
    )
  }
};



const anyElementHasValue = (elements) => elements.some(pipe(prop('value'), complement(isEmpty)));

const shouldSideBeRendered = (arr) => {
  if (isEmpty(arr)) return false;
  return arr.some(pipe(prop('elements'), anyElementHasValue));
};

export const ColumnCard = ({ elements }) => {
  const getElement = (name) => find(propEq('name', name))(elements) || {};
  const title = getElement(BLOCK_ELEMENTS.TITLE);
  const orderedElements = useMemo(() => sortBy(prop('order'))(elements), [elements]);
  const sideElements = [];
  const mainElements = [];

  const card = useCardLeft();

  orderedElements.forEach(
    cond([
      [
        propEq('name', 'SideCard'),
        (element) => {
          sideElements.push(element);
        },
      ],
      [
        propEq('name', 'ColumnCard'),
        (element) => {
          mainElements.push(element);
        },
      ],
    ])
  );

  const renderSideCard = ({ elements }, key) => {
    const { value: markdown } = findByType(ELEMENTS_TYPES.MARKDOWN, elements);
    const { value: connection } = findByType(ELEMENTS_TYPES.CONNECTION, elements);

    if (isEmpty(markdown) && isEmpty(connection)) return null;

    return <CardLeft elements={elements} key={key} />;
  };

  const renderSide = ({ value, id }) => {
    return (
      <Fragment key={id}>
        {renderWhenTrue(
          always(
            <CardToggleButton isVisible={card.isVisible} onClick={card.toggleVisibility}>
              <CardToggleCloseIcon />
            </CardToggleButton>
          )
        )(shouldSideBeRendered(value))}
        <CardContainer isVisible={card.isVisible} ref={card.containerRef}>
          {value.map(renderSideCard)}
        </CardContainer>
      </Fragment>
    );
  }

  const renderMain = ({ value = [] }, key) => (
    <CardWrapper key={key}>{sortBy(prop('order'))(value).map(renderColumnCard)}</CardWrapper>
  );
  
  return (
    <Container>
      <Side>{sideElements.map(renderSide)}</Side>
      <Main>
        {renderTitle(title)}
        {mainElements.map(renderMain)}
      </Main>
    </Container>
  );
};
